import { useRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useGallery,
  useContentToken,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';
import type { GalleryPhoto } from '../utils/heroImageUtils';
import { parseBackgroundVideo } from '../utils/heroVideoUtils';

/** Strip tags/entities so we can size the stacked hero title from plain length. */
function plainTextFromHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Long stacked names (e.g. "Dakota Commons Apartment Homes") overflow the
 * photo band at the default display size. Seed a smaller scale from word/char
 * count; a layout pass may shrink further to guarantee a fit.
 */
function heroTitleScale(plain: string): number {
  const words = plain ? plain.split(' ').filter(Boolean).length : 0;
  const chars = plain.length;
  if (words >= 6 || chars >= 40) return 0.55;
  if (words >= 5 || chars >= 30) return 0.65;
  if (words >= 4 || chars >= 22) return 0.8;
  return 1;
}

const HERO_TITLE_MIN_SCALE = 0.4;

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  fields: {
    video_url: {
      label: 'Background Video URL',
      type: 'url',
      defaultField: 'custom_property_featured_video',
      description: 'Optional. Vimeo, YouTube, or direct MP4/WebM. Falls back to image when empty.',
    },
    image: {
      label: 'Fallback / Poster Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Find Your Space',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// Foreground "feature" image slider that straddles the photo band and the white
// band on the right (desktop). Up to 5 slides, each is its own slot group bound
// to photo_gallery with a generator, so the AI field generator can pre-select
// the property's best standout photos (a different one per slide). At runtime
// the FeatureSlider below also filters photo_gallery to skip logos / floor
// plans / uncategorized entries (matching the /gallery page's curation) and
// substitutes a good photo whenever a slot resolves to one of those, so the
// rotator never shows a logo even when photo_gallery[N] happens to be one.
//
// NOTE: These are written as explicit object literals (not produced by a shared
// factory) on purpose, and every generator's `prompt`/`context` is an inline
// string literal — NOT a reference to a module-level const. The preview/editor
// extracts contentSchema via static regex + eval that only resolves sibling
// object-literal declarations (definitions assigned a braced object); a factory
// call (makeFeatureSlideDef(3)) or a plain string/number const reference is
// undefined in that eval,
// so the whole schema fails to extract and the generators never surface in the
// editor (and their selection rules never reach generation). Keep prompt/context
// as literals here even though it means repeating the text across slides.
const featureSlide1Def: SlotGroupDefinition = {
  label: 'Feature Slide 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout, high-quality photo of a desirable amenity or living space that works as a hero showcase image — favor appealing spaces such as pools, fitness centers, living rooms, kitchens, lobbies, lounges, and other attractive common areas. The image must be an actual photograph of a real space: never pick logos, brand marks, wordmarks, icons, signage, floor plans, maps, renderings, diagrams, screenshots, or any other non-photographic graphic. Strictly EXCLUDE unflattering, private, or utilitarian spaces: never pick bathrooms, restrooms, toilets, showers, or vanities; never pick laundry rooms, laundromats, washers, dryers, or utility/mechanical rooms; and avoid parking lots, garages, mailrooms, hallways, closets, bare dirt, or construction. When in doubt, skip the photo rather than showcase an unappealing space or a graphic.',
    context:
      'One of up to 5 rotating hero showcase slides — each slide must be a different, distinctive photo, and none should reuse the hero background/featured image or repeat another slide.',
    sourceCollection: 'photo_gallery',
  },
};

const featureSlide2Def: SlotGroupDefinition = {
  label: 'Feature Slide 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout, high-quality photo of a desirable amenity or living space that works as a hero showcase image — favor appealing spaces such as pools, fitness centers, living rooms, kitchens, lobbies, lounges, and other attractive common areas. The image must be an actual photograph of a real space: never pick logos, brand marks, wordmarks, icons, signage, floor plans, maps, renderings, diagrams, screenshots, or any other non-photographic graphic. Strictly EXCLUDE unflattering, private, or utilitarian spaces: never pick bathrooms, restrooms, toilets, showers, or vanities; never pick laundry rooms, laundromats, washers, dryers, or utility/mechanical rooms; and avoid parking lots, garages, mailrooms, hallways, closets, bare dirt, or construction. When in doubt, skip the photo rather than showcase an unappealing space or a graphic.',
    context:
      'One of up to 5 rotating hero showcase slides — each slide must be a different, distinctive photo, and none should reuse the hero background/featured image or repeat another slide.',
    sourceCollection: 'photo_gallery',
  },
};

const featureSlide3Def: SlotGroupDefinition = {
  label: 'Feature Slide 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout, high-quality photo of a desirable amenity or living space that works as a hero showcase image — favor appealing spaces such as pools, fitness centers, living rooms, kitchens, lobbies, lounges, and other attractive common areas. The image must be an actual photograph of a real space: never pick logos, brand marks, wordmarks, icons, signage, floor plans, maps, renderings, diagrams, screenshots, or any other non-photographic graphic. Strictly EXCLUDE unflattering, private, or utilitarian spaces: never pick bathrooms, restrooms, toilets, showers, or vanities; never pick laundry rooms, laundromats, washers, dryers, or utility/mechanical rooms; and avoid parking lots, garages, mailrooms, hallways, closets, bare dirt, or construction. When in doubt, skip the photo rather than showcase an unappealing space or a graphic.',
    context:
      'One of up to 5 rotating hero showcase slides — each slide must be a different, distinctive photo, and none should reuse the hero background/featured image or repeat another slide.',
    sourceCollection: 'photo_gallery',
  },
};

const featureSlide4Def: SlotGroupDefinition = {
  label: 'Feature Slide 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[3].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout, high-quality photo of a desirable amenity or living space that works as a hero showcase image — favor appealing spaces such as pools, fitness centers, living rooms, kitchens, lobbies, lounges, and other attractive common areas. The image must be an actual photograph of a real space: never pick logos, brand marks, wordmarks, icons, signage, floor plans, maps, renderings, diagrams, screenshots, or any other non-photographic graphic. Strictly EXCLUDE unflattering, private, or utilitarian spaces: never pick bathrooms, restrooms, toilets, showers, or vanities; never pick laundry rooms, laundromats, washers, dryers, or utility/mechanical rooms; and avoid parking lots, garages, mailrooms, hallways, closets, bare dirt, or construction. When in doubt, skip the photo rather than showcase an unappealing space or a graphic.',
    context:
      'One of up to 5 rotating hero showcase slides — each slide must be a different, distinctive photo, and none should reuse the hero background/featured image or repeat another slide.',
    sourceCollection: 'photo_gallery',
  },
};

const featureSlide5Def: SlotGroupDefinition = {
  label: 'Feature Slide 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[4].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout, high-quality photo of a desirable amenity or living space that works as a hero showcase image — favor appealing spaces such as pools, fitness centers, living rooms, kitchens, lobbies, lounges, and other attractive common areas. The image must be an actual photograph of a real space: never pick logos, brand marks, wordmarks, icons, signage, floor plans, maps, renderings, diagrams, screenshots, or any other non-photographic graphic. Strictly EXCLUDE unflattering, private, or utilitarian spaces: never pick bathrooms, restrooms, toilets, showers, or vanities; never pick laundry rooms, laundromats, washers, dryers, or utility/mechanical rooms; and avoid parking lots, garages, mailrooms, hallways, closets, bare dirt, or construction. When in doubt, skip the photo rather than showcase an unappealing space or a graphic.',
    context:
      'One of up to 5 rotating hero showcase slides — each slide must be a different, distinctive photo, and none should reuse the hero background/featured image or repeat another slide.',
    sourceCollection: 'photo_gallery',
  },
};
// Inner content for one slide (image or empty placeholder).
function SlideInner({ data }: { data: { image?: string | null; alt?: string | null } }) {
  return data.image ? (
    <VibesitesImage
      url={data.image}
      alt={data.alt || ''}
      objectFit="cover"
      className="hero-feature-img"
    />
  ) : (
    <div className="hero-feature-img hero-feature-img--empty" />
  );
}

// Auto-rotating slider of the feature slides. All five slot-group wrappers are
// always mounted with literal groupPaths (so each stays editable and the schema
// linter can match them); rotation only cycles the slides that resolve an image.
//
// Slot images are also filtered against a curated photo_gallery view (same
// exclusion set the /gallery page uses: logo / floorplan / other). If a slot's
// resolved image is not in the curated set — the common case is a property
// whose photo_gallery[0..N] happens to be a logo, so the raw defaultField
// pulls it in — we substitute the next unused curated photo instead. This is
// the reason the hero shows real amenity/interior shots even before an
// operator has hand-picked slides via the editor.
function FeatureSlider() {
  const s1 = useSlotGroup('hero.feature_slide_1', featureSlide1Def);
  const s2 = useSlotGroup('hero.feature_slide_2', featureSlide2Def);
  const s3 = useSlotGroup('hero.feature_slide_3', featureSlide3Def);
  const s4 = useSlotGroup('hero.feature_slide_4', featureSlide4Def);
  const s5 = useSlotGroup('hero.feature_slide_5', featureSlide5Def);

  const gallery = useGallery() as GalleryPhoto[];

  // Mirrors PhotoGallery's title filter so the hero rotator surfaces the same
  // curated set shown on the /gallery page — never logos, floor plans, or the
  // uncategorized "other" bucket (which is where marketing collateral, brand
  // marks, and utility snapshots typically land).
  //
  // Also drops photos whose title / alt text / description mentions
  // unflattering utility spaces (garages, parking, bathrooms, laundry rooms,
  // mailrooms, closets, mechanical rooms). These can be filed under real
  // categories like "Community" or "Amenity" — the exact-title check above
  // won't catch them, so we check keywords across the visible text fields.
  const curatedGallery = useMemo(() => {
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    const excludedKeywords = [
      'garage',
      'parking',
      'bathroom',
      'restroom',
      'toilet',
      'laundry',
      'utility',
      'mechanical',
      'mailroom',
      'closet',
    ];
    return (gallery || []).filter((p) => {
      if (!p?.image) return false;
      const title = (p?.title || '').toLowerCase().trim();
      if (excludedTitles.has(title)) return false;
      const haystack = `${title} ${(p?.alt_text || '').toLowerCase()} ${(p?.description || '').toLowerCase()}`;
      if (excludedKeywords.some((kw) => haystack.includes(kw))) return false;
      return true;
    });
  }, [gallery]);

  const curatedUrlSet = useMemo(
    () => new Set(curatedGallery.map((p) => p.image as string)),
    [curatedGallery],
  );

  // Resolve up to 5 slides to a curated photo. For each slot: if its image is
  // already curated (author or generator chose it), keep it; otherwise pull
  // the next unused curated photo from the queue. Any slide still empty after
  // this pass stays empty and drops out of rotation.
  const resolvedSlides = useMemo(() => {
    const rawSlots = [s1, s2, s3, s4, s5];
    const used = new Set<string>();
    const usableSlides: Array<{ image: string | null; alt: string }> = [];

    // First pass: honor any slot whose image is already in the curated set,
    // so hand-picked / generator-picked slides don't shuffle around.
    for (const slot of rawSlots) {
      if (slot.image && curatedUrlSet.has(slot.image) && !used.has(slot.image)) {
        used.add(slot.image);
        usableSlides.push({ image: slot.image, alt: slot.alt || 'Community photo' });
      } else {
        // placeholder — filled in the second pass or left null
        usableSlides.push({ image: null, alt: slot.alt || 'Community photo' });
      }
    }

    // Second pass: fill remaining slots from the curated queue, skipping any
    // photo already consumed by a slot in the first pass.
    let queueIdx = 0;
    for (let i = 0; i < usableSlides.length; i += 1) {
      if (usableSlides[i].image) continue;
      while (
        queueIdx < curatedGallery.length &&
        used.has(curatedGallery[queueIdx].image as string)
      ) {
        queueIdx += 1;
      }
      const next = curatedGallery[queueIdx];
      if (!next) break;
      used.add(next.image as string);
      usableSlides[i] = {
        image: next.image as string,
        alt: next.alt_text || usableSlides[i].alt,
      };
      queueIdx += 1;
    }

    return usableSlides;
    // useSlotGroup returns a stable object per render — only image/alt matter here.
  }, [s1, s2, s3, s4, s5, curatedGallery, curatedUrlSet]);

  const activeIdxs = useMemo(
    () =>
      resolvedSlides
        .map((slide, i) => (slide.image ? i : -1))
        .filter((i) => i >= 0),
    [resolvedSlides],
  );

  const [pos, setPos] = useState(0);

  useEffect(() => {
    if (activeIdxs.length <= 1) return;
    const id = setInterval(
      () => setPos((p) => (p + 1) % activeIdxs.length),
      5000,
    );
    return () => clearInterval(id);
  }, [activeIdxs.length]);

  const safePos = activeIdxs.length ? pos % activeIdxs.length : 0;
  const currentIdx = activeIdxs.length ? activeIdxs[safePos] : 0;
  const cls = (i: number) => `hero-feature-slide${i === currentIdx ? ' is-active' : ''}`;

  return (
    <div className="hero-feature">
      <div className="hero-feature-frame">
        <EditableSlotGroup groupPath="hero.feature_slide_1" definition={featureSlide1Def} className={cls(0)}>
          <SlideInner data={resolvedSlides[0]} />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="hero.feature_slide_2" definition={featureSlide2Def} className={cls(1)}>
          <SlideInner data={resolvedSlides[1]} />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="hero.feature_slide_3" definition={featureSlide3Def} className={cls(2)}>
          <SlideInner data={resolvedSlides[2]} />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="hero.feature_slide_4" definition={featureSlide4Def} className={cls(3)}>
          <SlideInner data={resolvedSlides[3]} />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="hero.feature_slide_5" definition={featureSlide5Def} className={cls(4)}>
          <SlideInner data={resolvedSlides[4]} />
        </EditableSlotGroup>
      </div>
    </div>
  );
}

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  const titleHtml = useContentToken('hero.title', {
    defaultField: 'property_name',
    defaultValue: '<p>Property Name</p>',
  });
  const titlePlain = plainTextFromHtml(titleHtml);
  const baseTitleScale = heroTitleScale(titlePlain);
  const [titleScale, setTitleScale] = useState(baseTitleScale);
  useComfyCtaStyles();

  // Parallax refs (image-only backgrounds)
  const sectionRef = useRef<HTMLElement>(null);
  const bandInnerRef = useRef<HTMLDivElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Seed from name length, then shrink so the stacked title stays inside the
  // photo band's *content* box (not the full band — large top padding for the
  // header/banner leaves much less room than clientHeight suggests).
  useLayoutEffect(() => {
    const container = bandInnerRef.current;
    if (!container) return;
    const title = container.querySelector('.hero-title') as HTMLElement | null;
    if (!title) return;
    const band = container.closest('.hero-band') as HTMLElement | null;

    const fits = (scale: number) => {
      container.style.setProperty('--hero-title-scale', String(scale));
      // Force layout with the candidate size before measuring.
      void title.offsetHeight;

      const cs = getComputedStyle(container);
      const padY =
        (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const available = container.clientHeight - padY;
      if (available <= 0) return false;
      if (title.scrollHeight > available + 0.5) return false;

      // Visual check against the photo band edge — catches flex-centering
      // overflow that scrollHeight alone can miss.
      if (band) {
        const bandBottom = band.getBoundingClientRect().bottom;
        const titleBottom = title.getBoundingClientRect().bottom;
        if (titleBottom > bandBottom - 4) return false;
      }
      return true;
    };

    const fit = () => {
      let lo = HERO_TITLE_MIN_SCALE;
      let hi = baseTitleScale;
      let best = HERO_TITLE_MIN_SCALE;
      // Prefer the seeded size when it already fits (short names).
      if (fits(baseTitleScale)) {
        best = baseTitleScale;
      } else {
        for (let i = 0; i < 12; i += 1) {
          const mid = (lo + hi) / 2;
          if (fits(mid)) {
            best = mid;
            lo = mid;
          } else {
            hi = mid;
          }
        }
      }
      container.style.setProperty('--hero-title-scale', String(best));
      setTitleScale((prev) => (Math.abs(prev - best) < 0.005 ? prev : best));
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(container);
    ro.observe(title);
    if (band) ro.observe(band);

    // Rubik loads async — re-fit after webfonts settle so metrics don't grow
    // past the band after the first paint.
    let cancelled = false;
    const fontsReady =
      typeof document !== 'undefined' && document.fonts?.ready
        ? document.fonts.ready.then(() => {
            if (!cancelled) fit();
          })
        : null;

    return () => {
      cancelled = true;
      ro.disconnect();
      void fontsReady;
    };
  }, [baseTitleScale, titlePlain]);

  const videoInfo = useMemo(
    () => parseBackgroundVideo(bgImage.video_url),
    [bgImage.video_url],
  );
  const showVideoBackground = !!videoInfo && !prefersReducedMotion;

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    setVideoReady(false);
    setIframeLoaded(false);
  }, [videoInfo?.src, videoInfo?.type]);

  useEffect(() => {
    if (!showVideoBackground || videoInfo?.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, [showVideoBackground, videoInfo]);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="rubik"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Rubik:wght@400;600;700;800;900&display=swap';
    link.setAttribute('data-vibe-font', 'rubik');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (showVideoBackground) return;
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, [showVideoBackground]);

  return (
    <section ref={sectionRef} className="hero-section relative w-full">
      {/* ---- Top photo band: background media + big headline ---- */}
      <div className="hero-band relative w-full overflow-hidden">
        <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
          {showVideoBackground && videoInfo ? (
            <div className="hero-bg-media absolute inset-0 bg-[var(--vibe-color-text)]">
              {bgImage.image && (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                    videoInfo.type === 'embed'
                      ? iframeLoaded
                        ? 'opacity-0'
                        : 'opacity-100'
                      : videoReady
                        ? 'opacity-0'
                        : 'opacity-100'
                  }`}
                />
              )}
              {videoInfo.type === 'embed' ? (
                <iframe
                  src={videoInfo.src}
                  className={`hero-video-iframe absolute inset-0 h-full w-full transition-opacity duration-1000 ${
                    iframeLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  allow="autoplay; fullscreen"
                  frameBorder="0"
                  onLoad={() => setIframeLoaded(true)}
                  title="Background video"
                />
              ) : (
                <video
                  ref={videoRef}
                  src={videoInfo.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                    videoReady ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              )}
            </div>
          ) : (
            /* Parallax + Ken Burns animated background */
            <div
              ref={imgWrapRef}
              className="hero-bg-media absolute h-[140%]"
              style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
            >
              {bgImage.image ? (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-cover"
                  style={{
                    animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                    willChange: 'transform',
                    transformOrigin: 'bottom right',
                    imageRendering: 'high-quality' as any,
                  }}
                />
              ) : (
                <div className="h-full w-full bg-[var(--vibe-color-text)]" />
              )}
            </div>
          )}
        </EditableSlotGroup>

        {/* Gradient overlay for headline contrast */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/45 to-black/30" />

        {/* Headline — left-aligned, sits over the photo. The editable property
            name is the entire h1; anything above/before it must be authored via
            hero.title so per-site overrides render correctly. Long names scale
            down via --hero-title-scale so they stay inside the photo band. */}
        <div
          ref={bandInnerRef}
          className="hero-band-inner relative z-10"
          style={{ ['--hero-title-scale' as string]: titleScale }}
        >
          <RichTextContent
            path="hero.title"
            as="h1"
            className="hero-entrance hero-entrance-1 hero-title"
          />
        </div>
      </div>

      {/* ---- Lower white band: subtitle + CTA on the left ---- */}
      <div className="hero-lower relative w-full">
        <div className="hero-lower-inner">
          <div className="hero-copy">
            <RichTextContent
              path="hero.subtitle"
              label="Subtitle"
              defaultValue="<p>Discover thoughtfully designed apartment homes built for the way you want to live.</p>"
              as="div"
              className="hero-entrance hero-entrance-2 hero-subtitle"
            />

            <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="hero-entrance hero-entrance-3 comfy-cta mt-7"
              >
                {ctaLink.label || 'Find Your Space'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      {/* ---- Feature image slider: straddles the two bands on the right (lg+),
              stacks as a full-width block below on mobile ---- */}
      <FeatureSlider />

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Split hero: a photographic top band with the headline, over a white
           lower band holding the subtitle + CTA, with a feature image
           straddling the two on the right (desktop). */
        .hero-section {
          isolation: isolate;
          --hero-max: 1440px;
          /* Band grows a bit taller than the raw title needs so the added top
             padding leaves the headline visually balanced (not crammed against
             the bottom of the photo band). */
          --hero-band-h: clamp(400px, 62vh, 620px);
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 1024px) {
          .hero-section { --hero-band-h: clamp(540px, 70vh, 840px); }
        }

        .hero-band {
          height: var(--hero-band-h);
        }

        /* Cover-fit Vimeo / YouTube iframe backgrounds. */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw;
          min-height: 100%;
          min-width: 177.78vh;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .hero-band-inner {
          --hero-pad-top-base: 7rem;
          max-width: var(--hero-max);
          margin: 0 auto;
          height: 100%;
          display: flex;
          align-items: center;
          /* Top padding clears the sticky banner + header. It eases off as
             --hero-title-scale drops so longer names reclaim vertical room.
             Bottom padding keeps the last line off the white-band edge. */
          padding: calc(
              var(--offer-banner-height, 0px) +
                (var(--hero-pad-top-base) * (0.55 + 0.45 * var(--hero-title-scale, 1)))
            )
            1.5rem 1.5rem;
        }
        @media (min-width: 768px) {
          .hero-band-inner {
            --hero-pad-top-base: 9rem;
            padding-left: 4.4rem;
            padding-right: 4.4rem;
            padding-bottom: 2rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-band-inner {
            --hero-pad-top-base: 11rem;
            padding-bottom: 2.5rem;
          }
        }

        .hero-title {
          font-family: "Rubik", var(--vibe-font-heading), sans-serif;
          /* --hero-title-scale shrinks longer property names so stacked lines
             don't spill past the bottom of the photo band. */
          font-size: calc(clamp(2.5rem, 11vw, 3.5rem) * var(--hero-title-scale, 1));
          font-weight: 800;
          line-height: 0.98;
          letter-spacing: -0.01em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 2px 20px rgba(0, 0, 0, 0.35);
          margin: 0 auto;
          max-width: 13ch;
          text-align: center;
        }
        .hero-title p,
        .hero-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-title {
            font-size: calc(clamp(3.5rem, 7vw, 5.25rem) * var(--hero-title-scale, 1));
            margin: 0;
            text-align: left;
          }
        }
        @media (min-width: 1024px) {
          .hero-title {
            font-size: calc(clamp(4.25rem, 5vw, 6rem) * var(--hero-title-scale, 1));
            max-width: 11ch;
          }
        }

        .hero-lower {
          background: var(--vibe-color-background, #fff);
        }
        .hero-lower-inner {
          max-width: var(--hero-max);
          margin: 0 auto;
          padding: 2.25rem 1.5rem 2.5rem;
        }
        @media (min-width: 768px) {
          .hero-lower-inner {
            padding-left: 4.4rem;
            padding-right: 4.4rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-lower { min-height: 250px; }
          /* Extra bottom room so the next section (PropertyOverview) — whose
             card pulls up with a -5rem overlap — lands on whitespace instead of
             covering the CTA. */
          .hero-lower-inner { padding-top: 3rem; padding-bottom: 8rem; }
        }
        .hero-copy {
          max-width: 34rem;
          margin: 0 auto;
          text-align: center;
        }
        @media (min-width: 768px) {
          .hero-copy { margin: 0; text-align: left; }
        }
        @media (min-width: 1024px) {
          .hero-copy { max-width: 46%; }
        }
        .hero-subtitle {
          font-family: var(--vibe-font-body), sans-serif;
          font-size: 1.0625rem;
          font-weight: 400;
          line-height: 1.6;
          color: var(--vibe-color-text);
          margin: 0 auto;
          max-width: 42ch;
        }
        @media (min-width: 768px) {
          .hero-subtitle { margin: 0; }
        }
        .hero-subtitle p { margin: 0; }
        .hero-subtitle p + p { margin-top: 0.75rem; }

        /* Feature slider — mobile: a centered block flowing below the copy,
           narrower than full-bleed so the card feels lighter. Keep the bottom
           padding tight so the next section sits close. */
        .hero-feature {
          display: block;
          width: 100%;
          max-width: 22rem;
          margin: 0 auto;
          padding: 0 1.5rem 2rem;
        }
        @media (min-width: 768px) {
          .hero-feature { max-width: 26rem; padding: 0 4.4rem 2.5rem; }
        }
        .hero-feature-frame {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          box-shadow: 0 18px 50px rgba(0, 0, 0, 0.22);
        }
        .hero-feature-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.8s ease;
        }
        .hero-feature-slide.is-active {
          opacity: 1;
        }
        .hero-feature-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .hero-feature-img--empty {
          background: var(--vibe-color-surface);
        }

        /* Desktop: absolutely position the slider so it straddles the photo
           band and the white band, aligned to the content container's right
           edge. */
        @media (min-width: 1024px) {
          .hero-feature {
            position: absolute;
            z-index: 15;
            max-width: none;
            margin: 0;
            padding: 0;
            top: calc(var(--hero-band-h) * 0.45);
            width: min(36vw, 500px);
            aspect-ratio: 1 / 1;
            /* Right-anchored to the content container. The extra
               min(10vw, 140px) term nudges the slider leftward by the same
               amount the width was reduced (from min(46vw, 640px) to
               min(36vw, 500px)), so the left edge lands near its pre-resize
               position instead of drifting rightward with the smaller size. */
            right: calc(max(2.5rem, calc((100% - var(--hero-max)) / 2 + 4.4rem)) + min(10vw, 140px));
          }
          .hero-feature-frame {
            height: 100%;
            aspect-ratio: auto;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Property Name</p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Discover thoughtfully designed apartment homes built for the way you want to live.</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
    'hero.feature_slide_1': featureSlide1Def,
    'hero.feature_slide_2': featureSlide2Def,
    'hero.feature_slide_3': featureSlide3Def,
    'hero.feature_slide_4': featureSlide4Def,
    'hero.feature_slide_5': featureSlide5Def,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
